import { GridContainer } from "@/components/grid-container/GridContainer";
import { PageLayout } from "@/components/page-layout/PageLayout";
import { useLocalError } from "@/hooks/error/UseLocalError";
import { ZIndex } from "@/lib/utils/ZIndexEnum";
import { Box, Column } from "@stenajs-webui/core";
import { Banner, Card, CardBody } from "@stenajs-webui/elements";
import { useContext, useEffect, useState } from "react";
import { bookingDataExist, customerDataExist } from "./lib/helpers";
import { renderError } from "./lib/renderError";
import { useBookingPriceCalculation } from "./hooks/UseBookingPriceCalculation";
import { useCustomerPriceCalculation } from "./hooks/UseCustomerPriceCalculation";
import { PriceCalculatorActionBar } from "./components/PriceCalculatorActionBar";
import { PriceCalculatorCustomerSearchResultPane } from "./components/PriceCalculatorCustomerSearchResultPane";
import { PriceCalculatorCustomerForm } from "./components/price-calculator-customer-form/PriceCalculatorCustomerForm";
import { PriceCalculatorBookingForm } from "./components/PriceCalculatorBookingForm";
import { PriceCalculatorBookingResultPane } from "./components/PriceCalculatorBookingResultPane";
import { PriceCalculatorSearch } from "./components/PriceCalculatorSearch";
import {
  PriceCalculatorContext,
  PriceCalculatorContextType,
} from "./PriceCalculatorContext";
import { SearchEntityType } from "./types/types";

export const PriceCalculatorFormFetcher: React.FC = () => {
  const [priceUpdatePending, setPriceUpdatePending] = useState(false);

  const { localError, setLocalError, clearLocalError } = useLocalError();
  const {
    formData,
    onChangeFormData,
    customerCalculatedState,
    setCustomerCalculatedState,
    defaultFormState,
    customerResult,
    setCustomerResult,
    bookingResult,
    setBookingResult,
  } = useContext(PriceCalculatorContext) as PriceCalculatorContextType;
  const {
    fetchPriceCalculatorBooking,
    loading,
    error: calculateBookingError,
  } = useBookingPriceCalculation(
    setBookingResult,
    formData.includePendingOffered
  );

  const {
    calculateCustomerPrice,
    loading: customerPriceCalculationLoading,
    error: customerSearchError,
  } = useCustomerPriceCalculation();

  useEffect(() => {
    (customerSearchError || calculateBookingError) &&
      setLocalError(customerSearchError || calculateBookingError);
  }, [customerSearchError, calculateBookingError, setLocalError]);

  useEffect(() => {
    const calculationStateHasChanged =
      JSON.stringify({ ...formData }) !==
      JSON.stringify({ ...customerCalculatedState });

    setPriceUpdatePending(calculationStateHasChanged);
  }, [formData, customerCalculatedState, setPriceUpdatePending]);

  const doesHaveCustomerData = customerDataExist({
    customerSearchResult: customerResult,
    searchEntity: customerCalculatedState.searchEntityType,
  });

  const doesHaveBookingData = bookingDataExist({
    bookingSearchResult: bookingResult,
    searchEntity: formData.searchEntityType,
  });

  const onCalculatePrice = async (useCustomerInvoiceCurrency = true) => {
    if (formData.searchEntityType === SearchEntityType.CUSTOMER) {
      setCustomerCalculatedState(defaultFormState);
      setCustomerResult(undefined);
      setPriceUpdatePending(false);
      calculateCustomerPrice(formData, useCustomerInvoiceCurrency);
    } else if (formData.version !== undefined && formData.bookingNumber) {
      setBookingResult(undefined);
      clearLocalError();
      await fetchPriceCalculatorBooking({
        variables: {
          bookingNum: String(formData.bookingNumber),
          consignmentVersion: formData.version,
        },
      });
    }
  };
  return (
    <Box>
      <PageLayout>
        <GridContainer boxMinWidth={800} boxMaxWidth={"0.5fr"}>
          <Column height={"100%"}>
            <Card zIndex={ZIndex.none}>
              <CardBody>
                <PriceCalculatorSearch />
                {formData.searchEntityType === SearchEntityType.CUSTOMER ? (
                  <PriceCalculatorCustomerForm />
                ) : (
                  <PriceCalculatorBookingForm
                    loading={loading}
                    clearLocalError={clearLocalError}
                    localError={localError}
                  />
                )}
              </CardBody>
            </Card>
          </Column>
          <Box gap={3}>
            <PriceCalculatorActionBar
              formData={formData}
              loading={customerPriceCalculationLoading || loading}
              onCalculatePrice={() => onCalculatePrice()}
              onChangeFormData={onChangeFormData}
            />

            {priceUpdatePending &&
              customerResult &&
              !customerPriceCalculationLoading && (
                <Box>
                  <Banner
                    headerText={
                      "Customer and/or sailing details have been changed"
                    }
                    text={"You need to recalculate the price"}
                    variant={"info"}
                  />
                </Box>
              )}

            {localError &&
              formData.searchEntityType === SearchEntityType.CUSTOMER &&
              renderError(localError, clearLocalError)}

            {doesHaveBookingData && (
              <PriceCalculatorBookingResultPane bookingResult={bookingResult} />
            )}
            {doesHaveCustomerData && (
              <PriceCalculatorCustomerSearchResultPane
                onCalculatePrice={onCalculatePrice}
              />
            )}
          </Box>
        </GridContainer>
      </PageLayout>
    </Box>
  );
};
